import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Clock } from "lucide-react";

interface SummaryActionsSkeletonProps {
  isDarkMode?: boolean;
  count?: number;
}

const SummaryActionsSkeleton: React.FC<SummaryActionsSkeletonProps> = ({
  isDarkMode = false,
  count = 1,
}) => {
  const cardBg = isDarkMode ? "bg-gray-700" : "bg-gray-50";
  const cardBorder = isDarkMode ? "border-gray-600" : "border-gray-200";
  const skeleton = isDarkMode ? "bg-gray-600" : "bg-gray-200";
  const innerBg = isDarkMode ? "bg-gray-800" : "bg-white";
  const innerBorder = isDarkMode ? "border-gray-600" : "border-gray-300";

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={`summary-skeleton-${index}`}
          className={`p-3 rounded-lg border ${cardBg} ${cardBorder}`}
        >
          <div className="flex items-start gap-3">
            {/* Checkbox */}
            <div className="mt-1">
              <Checkbox disabled />
            </div>

            <div className="flex-1">
              {/* Title row */}
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-4 h-4 rounded ${skeleton} `} />
                <div className={`h-4 w-40 rounded ${skeleton} `} />
                <div className={`h-4 w-16 rounded ${skeleton} `} />
              </div>

              {/* Vitals skeleton block */}
              <div className="mt-4 ml-1">
                <div
                  className={`p-4 rounded-lg border ${innerBg} ${innerBorder}`}
                >
                  <div className={`h-4 w-32 mb-3 rounded ${skeleton} `} />

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 text-xs">
                      <thead>
                        <tr>
                          <th className="border border-gray-300 p-1">
                            <div className={`h-3 w-12 rounded ${skeleton} `} />
                          </th>
                          <th className="border border-gray-300 p-1">
                            <div className={`h-3 w-12 rounded ${skeleton} `} />
                          </th>
                          <th className="border border-gray-300 p-1">
                            <div className={`h-3 w-12 rounded ${skeleton} `} />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1, 2, 3].map((row) => (
                          <tr key={row}>
                            <td className="border border-gray-300 p-1">
                              <div
                                className={`h-3 w-20 rounded ${skeleton} `}
                              />
                            </td>
                            <td className="border border-gray-300 p-1">
                              <div
                                className={`h-3 w-24 rounded ${skeleton} `}
                              />
                            </td>
                            <td className="border border-gray-300 p-1">
                              <div
                                className={`h-3 w-28 rounded ${skeleton} `}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Details skeleton */}
              <div className="mt-2 ml-6">
                <div
                  className={`w-full h-24 rounded border ${innerBorder} ${skeleton} `}
                />
              </div>

              {/* Timestamp */}
              <div
                className={`flex items-center gap-1 mt-2 text-xs ml-6 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <Clock className="w-3 h-3 opacity-50" />
                <div className={`h-3 w-32 rounded ${skeleton} `} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default SummaryActionsSkeleton;
